import { ReactNode } from 'react';
import { Money } from 'types';
import { PendingScheduledPayment } from 'lib/bookingInfo';
import { ContentHeading } from 'components/Typography';
import MessageBoard from 'components/MessageBoard';
import { PaymentsCard, LineItem } from 'components/payments/primitives';
import { HorizontalRule } from 'components/Layout';
import { FormattedMoney } from 'components/Formatters';
import PayableAtPropertyLineItems from './PayableAtPropertyLineItems';
import PaymentTotal from './PaymentTotal';
import { PayableAtPropertyBreakdown } from './helpers/getExcludePayableAtPropertyLabel';

export interface ChargesSummaryProps {
  isCancelled: boolean;
  nights: number;
  baseAmount: Money;
  discountAmount?: Money;
  offerTotalAmounts: Array<Money>;
  formattedCheckIn: string;
  payableAtPropertyTotal?: Money;
  payableAtPropertyBreakdown?: PayableAtPropertyBreakdown;
  paidToDateAmounts: Array<Money>;
  pendingScheduledPayment?: PendingScheduledPayment;
  ActionComponent?: ReactNode;
}

const ChargesSummary = ({
  isCancelled,
  nights,
  baseAmount,
  discountAmount,
  offerTotalAmounts,
  formattedCheckIn,
  payableAtPropertyTotal,
  payableAtPropertyBreakdown,
  paidToDateAmounts,
  pendingScheduledPayment,
  ActionComponent,
}: ChargesSummaryProps) => (
  <>
    <ContentHeading>Booking charges {ActionComponent}</ContentHeading>
    {!isCancelled && payableAtPropertyTotal && (
      <MessageBoard data-testid="payable-at-property-total" mb={5} borderColor="lightOrange" bg="lightOrange">
        <b>
          Your booking has a charge of <FormattedMoney {...payableAtPropertyTotal} /> that is payable at the property at check-in.
        </b>{' '}
        This must be paid in local currency when you check-in.
      </MessageBoard>
    )}
    <PaymentsCard>
      <LineItem data-testid="booking-night-amount" title={`${nights} x night${nights > 1 ? 's' : ''}`} totals={[baseAmount]} />
      {discountAmount && <LineItem data-testid="voucher-amount" title="Discount voucher" totals={[discountAmount]} />}
      {payableAtPropertyBreakdown && (
        <PayableAtPropertyLineItems
          data-testid="payable-at-property-lineitems"
          formattedCheckIn={formattedCheckIn}
          payableAtPropertyBreakdown={payableAtPropertyBreakdown}
        />
      )}
      <HorizontalRule mb={4} mx={5} />
      <LineItem data-testid="booking-total" title="Booking total" totals={offerTotalAmounts} />
      <PaymentTotal
        data-testid="total-paid"
        payableAtPropertyBreakdown={payableAtPropertyBreakdown}
        paidToDateAmounts={paidToDateAmounts}
        pendingScheduledPayment={pendingScheduledPayment}
      />
    </PaymentsCard>
  </>
);

export default ChargesSummary;
